import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../../axios/axiosInstance';
import { Country, State, District, Taluk, City } from './types';
import LocationList from './components/LocationList';

const LocationManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'country' | 'state' | 'district' | 'taluk' | 'city'>('country');
  const [loading, setLoading] = useState(false);
  const [isSeeded, setIsSeeded] = useState(false);
  const [isSeeding, setIsSeeding] = useState(false);
  const [checkingSeedStatus, setCheckingSeedStatus] = useState(true);
  const [showSeededBanner, setShowSeededBanner] = useState(false);

  // Data states
  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [taluks, setTaluks] = useState<Taluk[]>([]);
  const [cities, setCities] = useState<City[]>([]);

  // Filter states
  const [selectedCountry, setSelectedCountry] = useState<number | null>(null);
  const [selectedState, setSelectedState] = useState<number | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<number | null>(null);
  const [selectedTaluk, setSelectedTaluk] = useState<number | null>(null);

  // Search states
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch functions
  const fetchCountries = async (search?: string) => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const url = `/api/country${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await axiosInstance.get<{ status: string; data: Country[] }>(url);
      if (res.status === 200) {
        setCountries(res.data.data || []);
      }
    } catch (error: any) {
      toast.error('Failed to fetch countries');
    }
  };

  const fetchStates = async (countryId?: number, search?: string) => {
    try {
      const params = new URLSearchParams();
      if (countryId) params.append('country_id', countryId.toString());
      if (search) params.append('search', search);
      const url = `/api/state${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await axiosInstance.get<{ status: string; data: State[] }>(url);
      if (res.status === 200) {
        setStates(res.data.data || []);
      }
    } catch (error: any) {
      toast.error('Failed to fetch states');
    }
  };

  const fetchDistricts = async (stateId?: number, search?: string) => {
    try {
      const params = new URLSearchParams();
      if (stateId) params.append('state_id', stateId.toString());
      if (search) params.append('search', search);
      const url = `/api/district${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await axiosInstance.get<{ status: string; data: District[] }>(url);
      if (res.status === 200) {
        setDistricts(res.data.data || []);
      }
    } catch (error: any) {
      toast.error('Failed to fetch districts');
    }
  };

  const fetchTaluks = async (districtId?: number, search?: string) => {
    try {
      const params = new URLSearchParams();
      if (districtId) params.append('district_id', districtId.toString());
      if (search) params.append('search', search);
      const url = `/api/taluk${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await axiosInstance.get<{ status: string; data: Taluk[] }>(url);
      if (res.status === 200) {
        setTaluks(res.data.data || []);
      }
    } catch (error: any) {
      toast.error('Failed to fetch taluks');
    }
  };

  const fetchCities = async (talukId?: number, search?: string) => {
    try {
      const params = new URLSearchParams();
      if (talukId) params.append('taluk_id', talukId.toString());
      if (search) params.append('search', search);
      const url = `/api/city${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await axiosInstance.get<{ status: string; data: City[] }>(url);
      if (res.status === 200) {
        setCities(res.data.data || []);
      }
    } catch (error: any) {
      toast.error('Failed to fetch cities');
    }
  };

  // Check seed status
  const checkSeedStatus = async () => {
    try {
      const res = await axiosInstance.get<{ status: string; data: { is_seeded: boolean } }>('/api/location-seed/status');
      if (res.status === 200) {
        setIsSeeded(res.data.data?.is_seeded || false);
      }
    } catch (error: any) {
      console.error('Failed to check seed status:', error);
      setIsSeeded(false);
    } finally {
      setCheckingSeedStatus(false);
    }
  };

  // Seed location data
  const handleSeedData = async () => {
    if (!window.confirm('This will seed Kanyakumari location data (India → Tamil Nadu → Kanyakumari → Taluks → Cities). Continue?')) {
      return;
    }

    setIsSeeding(true);
    try {
      const res = await axiosInstance.post<{ status: string; data: any; message?: string }>('/api/location-seed');
      if (res.status === 200) {
        toast.success(res.data.message || 'Kanyakumari data seeded successfully!');
        setIsSeeded(true);
        setShowSeededBanner(true);
        // Hide banner after 5 seconds
        setTimeout(() => {
          setShowSeededBanner(false);
        }, 5000);
        // Refresh all data
        await Promise.all([
          fetchCountries(),
          fetchStates(),
          fetchDistricts(),
          fetchTaluks(),
          fetchCities(),
        ]);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to seed location data');
    } finally {
      setIsSeeding(false);
    }
  };

  // Initial load
  useEffect(() => {
    checkSeedStatus();
    fetchCountries();
    fetchStates();
    fetchDistricts();
    fetchTaluks();
    fetchCities();
  }, []);

  // Refresh handlers
  const handleRefresh = () => {
    switch (activeTab) {
      case 'country':
        fetchCountries(searchQuery || undefined);
        break;
      case 'state':
        fetchStates(selectedCountry || undefined, searchQuery || undefined);
        break;
      case 'district':
        fetchDistricts(selectedState || undefined, searchQuery || undefined);
        break;
      case 'taluk':
        fetchTaluks(selectedDistrict || undefined, searchQuery || undefined);
        break;
      case 'city':
        fetchCities(selectedTaluk || undefined, searchQuery || undefined);
        break;
    }
  };

  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      switch (activeTab) {
        case 'country':
          fetchCountries(searchQuery || undefined);
          break;
        case 'state':
          fetchStates(selectedCountry || undefined, searchQuery || undefined);
          break;
        case 'district':
          fetchDistricts(selectedState || undefined, searchQuery || undefined);
          break;
        case 'taluk':
          fetchTaluks(selectedDistrict || undefined, searchQuery || undefined);
          break;
        case 'city':
          fetchCities(selectedTaluk || undefined, searchQuery || undefined);
          break;
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, activeTab, selectedCountry, selectedState, selectedDistrict, selectedTaluk]);

  // Reset search when tab changes
  useEffect(() => {
    setSearchQuery('');
  }, [activeTab]);

  // Filter change handlers
  const handleCountryFilterChange = (id: number | null) => {
    setSelectedCountry(id);
    setSelectedState(null);
    setSelectedDistrict(null);
    setSelectedTaluk(null);
    fetchStates(id || undefined, searchQuery || undefined);
  };

  const handleStateFilterChange = (id: number | null) => {
    setSelectedState(id);
    setSelectedDistrict(null);
    setSelectedTaluk(null);
    fetchDistricts(id || undefined, searchQuery || undefined);
  };

  const handleDistrictFilterChange = (id: number | null) => {
    setSelectedDistrict(id);
    setSelectedTaluk(null);
    fetchTaluks(id || undefined, searchQuery || undefined);
  };

  const handleTalukFilterChange = (id: number | null) => {
    setSelectedTaluk(id);
    fetchCities(id || undefined, searchQuery || undefined);
  };

  const tabs = [
    { id: 'country' as const, label: 'Countries', icon: 'fa-solid fa-globe' },
    { id: 'state' as const, label: 'States', icon: 'fa-solid fa-map' },
    { id: 'district' as const, label: 'Districts', icon: 'fa-solid fa-map-location-dot' },
    { id: 'taluk' as const, label: 'Taluks', icon: 'fa-solid fa-location-dot' },
    { id: 'city' as const, label: 'Cities', icon: 'fa-solid fa-city' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
      <div className="max-w-[1600px] mx-auto">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Location Management
              </h1>
              <p className="text-gray-600 dark:text-gray-300">
                Manage geographic hierarchy: Country → State → District → Taluk → City
              </p>
            </div>
            <button
              onClick={handleSeedData}
              disabled={isSeeded || isSeeding || checkingSeedStatus}
              className="btn-primary flex items-center gap-2 px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              title={isSeeded ? 'Location data has already been seeded' : 'Seed Kanyakumari location data'}
            >
              {checkingSeedStatus ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                  <span>Checking...</span>
                </>
              ) : isSeeding ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                  <span>Seeding...</span>
                </>
              ) : isSeeded ? (
                <>
                  <i className="fa-solid fa-check-circle"></i>
                  <span>Data Seeded</span>
                </>
              ) : (
                <>
                  <i className="fa-solid fa-seedling"></i>
                  <span>Seed Kanyakumari Data</span>
                </>
              )}
            </button>
          </div>
          {showSeededBanner && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center gap-3 animate-in fade-in">
              <i className="fa-solid fa-circle-check text-green-600 dark:text-green-400"></i>
              <div className="flex-1">
                <p className="text-sm font-semibold text-green-800 dark:text-green-300">
                  Location data has been seeded
                </p>
                <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                  Kanyakumari location data (India → Tamil Nadu → Kanyakumari → 4 Taluks → 130+ Cities) is now available
                </p>
              </div>
              <button
                onClick={() => setShowSeededBanner(false)}
                className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 mb-6">
          <div className="flex overflow-x-auto border-b border-gray-200 dark:border-gray-700">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-4 font-semibold text-sm transition-colors whitespace-nowrap border-b-2 ${
                  activeTab === tab.id
                    ? 'border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <i className={`${tab.icon} mr-2`}></i>
                {tab.label}
              </button>
            ))}
          </div>
          
          {/* Search Bar */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${tabs.find(t => t.id === activeTab)?.label.toLowerCase()}...`}
                className="dark-input w-full px-4 py-2.5 pr-10"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">
                <i className="fa-solid fa-search"></i>
              </span>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div>
          {activeTab === 'country' && (
            <LocationList
              entityType="country"
              data={countries}
              onRefresh={handleRefresh}
            />
          )}

          {activeTab === 'state' && (
            <LocationList
              entityType="state"
              data={states}
              onRefresh={handleRefresh}
              parentOptions={countries.map(c => ({ id: c.id, name: c.name }))}
              parentLabel="Country"
              parentFilter={selectedCountry}
              onParentFilterChange={handleCountryFilterChange}
            />
          )}

          {activeTab === 'district' && (
            <LocationList
              entityType="district"
              data={districts}
              onRefresh={handleRefresh}
              parentOptions={states.map(s => ({ id: s.id, name: s.name }))}
              parentLabel="State"
              parentFilter={selectedState}
              onParentFilterChange={handleStateFilterChange}
            />
          )}

          {activeTab === 'taluk' && (
            <LocationList
              entityType="taluk"
              data={taluks}
              onRefresh={handleRefresh}
              parentOptions={districts.map(d => ({ id: d.id, name: d.name }))}
              parentLabel="District"
              parentFilter={selectedDistrict}
              onParentFilterChange={handleDistrictFilterChange}
            />
          )}

          {activeTab === 'city' && (
            <LocationList
              entityType="city"
              data={cities}
              onRefresh={handleRefresh}
              parentOptions={taluks.map(t => ({ id: t.id, name: t.name }))}
              parentLabel="Taluk"
              parentFilter={selectedTaluk}
              onParentFilterChange={handleTalukFilterChange}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationManagement;

